import React, { useMemo } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { TaskEntry } from '../TaskEntry'
import type { TaskEntryProps } from '../TaskEntry'

interface SortableTaskEntryProps extends TaskEntryProps {
  /** Task ID used as the sortable identifier */
  sortableId: string
  /** Whether this item is part of a dragged group but not the active drag item */
  isDraggedGroup?: boolean
  /** Number of items being dragged (shown as badge on active item) */
  dragCount?: number
  /** True while the DragOverlay is showing this item (keeps original hidden) */
  isOverlayActive?: boolean
  /** True when the active drag item is over a different segment */
  isCrossSegmentDrag?: boolean
  /** True while any drag is in progress — enables sortable transitions */
  isDragActive?: boolean
}

const SortableTaskEntryComponent: React.FC<SortableTaskEntryProps> = ({
  sortableId,
  isDraggedGroup,
  dragCount,
  isOverlayActive,
  isCrossSegmentDrag,
  isDragActive,
  ...taskEntryProps
}) => {
  // Disable sorting when the item is being edited/created.
  // This prevents Space key in text inputs from triggering a drag.
  const isDisabled = taskEntryProps.mode === 'expanded' || taskEntryProps.mode === 'creating'

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: sortableId,
    disabled: isDisabled,
    // Only enable transitions while a drag is active — smooth reorder during drag.
    // After the drop, transitions are off so any transform changes (from useLiveQuery
    // catching up, optimistic update cleanup, or dnd-kit reset) happen instantly
    // instead of causing a visible "double positioning" animation.
    transition: isDragActive ? { duration: 300, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' } : null,
    // Disable FLIP animations on layout changes. After a drop, clearing
    // optimisticUpdates can briefly revert items to old order before useLiveQuery
    // catches up. The default animateLayoutChanges would compute stale transforms
    // during that window, causing visible displacement.
    animateLayoutChanges: () => false,
  })

  // Expanded/creating: clear all dnd-kit visual effects so stale transforms
  // from a recent drag don't interfere with the expanded card layout.
  // Collapsed: standard dnd-kit styling (hidden active item, group opacity, etc.)
  // No opacity transition needed — DragOverlay covers the source item seamlessly
  // on drag start, and all items appear simultaneously on drop (via batched state clear).
  const style = useMemo<React.CSSProperties>(
    () =>
      isDisabled
        ? { position: 'relative' }
        : {
            transform: CSS.Transform.toString(transform),
            transition: transition ?? undefined,
            opacity: (isDragging || isOverlayActive) ? 0 : undefined,
            zIndex: isDragging ? 10 : undefined,
            position: 'relative',
          },
    [isDisabled, transform, transition, isDragging, isOverlayActive]
  )

  // Collapse group items (not the active drag item) with CSS grid trick.
  // Also collapse the active item when dragging cross-segment (close the gap
  // in the source segment — the target segment opens its own gap via strategy).
  // grid-template-rows transitions smoothly between 1fr and 0fr.
  const isCollapsing =
    (isDraggedGroup && !isDragging && !isOverlayActive) ||
    (isOverlayActive && isCrossSegmentDrag)

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-task-id={sortableId}
      className={isCollapsing ? 'sortable-task-entry--collapsing' : undefined}
      {...attributes}
      {...listeners}
    >
      <TaskEntry {...taskEntryProps} />
    </div>
  )
}

// Memoized so a re-render of TaskList (e.g. editingTaskId flipping on one row)
// only re-renders the rows whose props actually changed. Requires TaskList to
// pass reference-stable handlers — otherwise inline arrows defeat the memo.
export const SortableTaskEntry = React.memo(SortableTaskEntryComponent)
